import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { widgetConfigApi, WidgetConfig, WidgetButtonConfig, WidgetWindowConfig, WidgetPrechatConfig } from '../services/api';

// Default configuration (fallback if API fails)
const defaultButtonConfig: WidgetButtonConfig = {
  type: 'adaptive',
  position: 'bottom_right',
  offsetX: 20,
  offsetY: 20,
  mobileEnabled: true,
  textContent: null,
  onlineImageUrl: null,
  offlineImageUrl: null,
};

const defaultWindowConfig: WidgetWindowConfig = {
  style: 'modern',
  primaryColor: '#6366f1',
  secondaryColor: '#ffffff',
  headerType: 'agent_info',
  headerText: null,
  bannerImageUrl: null,
  showAgentAvatar: true,
  backgroundType: 'solid',
  backgroundValue: null,
  greetingMessage: null,
  windowType: 'embedded',
  customCss: null,
  autoEndChatTimeout: 30,
  features: {
    soundNotifications: true,
    fileSend: true,
    emojis: true,
    downloadTranscript: false,
    printTranscript: false,
    emailTranscript: false,
    offlineSwitch: true,
    showUnreadDot: true,
    audioChat: false,
    videoChat: false,
    removePoweredBy: true,
  },
};

const defaultPrechatConfig: WidgetPrechatConfig = {
  enabled: true,
  formType: 'form',
  greetingMessage: null,
  fields: [],
};

const defaultI18n: Record<string, string> = {
  chatTitle: 'Sohbet',
  supportTitle: 'Canli Destek',
  sendButton: 'Gonder',
  placeholder: 'Mesajinizi yazin...',
  connecting: 'Baglaniyor...',
  connected: 'Bagli',
  disconnected: 'Baglanti kesildi',
  typing: 'yaziyor...',
  online: 'Cevrimici',
  offline: 'Cevrimdisi',
  startChat: 'Sohbet Baslat',
  endChat: 'Sohbeti Sonlandir',
  closeChat: 'Kapat',
  minimizeChat: 'Kucult',
  newMessage: 'Yeni mesaj',
  prechatTitle: 'Nece komek ede bilerik?',
  prechatSubtitle: '* gerekli alanlari belirtir',
  nameLabel: 'Isminiz',
  namePlaceholder: 'Adinizi girin',
  emailLabel: 'E-posta',
  emailPlaceholder: 'E-posta adresinizi girin',
  messageLabel: 'Mesajiniz',
  messagePlaceholder: 'Mesajinizi yazin...',
  subjectLabel: 'Konu',
  selectSubject: 'Konu secin',
  'subject.payment': 'Odeme Sorunu',
  'subject.account': 'Hesap Sorunu',
  'subject.technical': 'Teknik Destek',
  'subject.bonus': 'Bonus ve Promosyonlar',
  'subject.game': 'Oyun Sorunu',
  'subject.other': 'Diger',
  waitingForAgent: 'Temsilci bekleniyor...',
  agentJoined: 'Temsilci sohbete katildi',
  chatEnded: 'Sohbet sonlandi',
  connectionError: 'Baglanti hatasi',
  sendError: 'Mesaj gonderilemedi',
  requiredField: 'Bu alan zorunludur',
};

const defaultConfig: WidgetConfig = {
  enabled: true,
  button: defaultButtonConfig,
  window: defaultWindowConfig,
  prechat: defaultPrechatConfig,
  i18n: defaultI18n,
};

interface WidgetConfigContextType {
  config: WidgetConfig;
  isLoading: boolean;
  error: string | null;
  reloadConfig: () => Promise<void>;
  t: (key: string) => string;
}

const WidgetConfigContext = createContext<WidgetConfigContextType | undefined>(undefined);

interface WidgetConfigProviderProps {
  children: ReactNode;
}

export const WidgetConfigProvider: React.FC<WidgetConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<WidgetConfig>(defaultConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('[WidgetConfig] Loading config from API...');
      const result = await widgetConfigApi.getConfig();
      console.log('[WidgetConfig] API Response:', result);

      if (result.success && result.data) {
        console.log('[WidgetConfig] Prechat fields from API:', result.data.prechat?.fields);
        // Merge with defaults to ensure all fields exist
        const mergedConfig = {
          ...defaultConfig,
          ...result.data,
          button: { ...defaultButtonConfig, ...result.data.button },
          window: {
            ...defaultWindowConfig,
            ...result.data.window,
            features: {
              ...defaultWindowConfig.features,
              ...result.data.window?.features,
            },
          },
          prechat: { ...defaultPrechatConfig, ...result.data.prechat },
          i18n: { ...defaultI18n, ...result.data.i18n },
        };
        console.log('[WidgetConfig] Merged config prechat:', mergedConfig.prechat);
        setConfig(mergedConfig);
      } else {
        console.warn('[WidgetConfig] Failed to load widget config, using defaults:', result.error);
        setError(result.error || 'Failed to load configuration');
        // Keep default config on error
      }
    } catch (err) {
      console.error('[WidgetConfig] Error loading widget config:', err);
      setError('Failed to load widget configuration');
      // Keep default config on error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  // Inject custom CSS from widget config
  useEffect(() => {
    const customCss = config.window?.customCss;
    const styleId = 'widget-custom-css';

    // Remove existing custom style if any
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Add new custom CSS if provided
    if (customCss) {
      const styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = customCss;
      document.head.appendChild(styleElement);
    }

    // Cleanup on unmount
    return () => {
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    };
  }, [config.window?.customCss]);

  // Translation helper function
  const t = (key: string): string => {
    return config.i18n[key] || key;
  };

  return (
    <WidgetConfigContext.Provider
      value={{
        config,
        isLoading,
        error,
        reloadConfig: loadConfig,
        t,
      }}
    >
      {children}
    </WidgetConfigContext.Provider>
  );
};

export const useWidgetConfig = (): WidgetConfigContextType => {
  const context = useContext(WidgetConfigContext);
  if (context === undefined) {
    throw new Error('useWidgetConfig must be used within a WidgetConfigProvider');
  }
  return context;
};

// Export types for use in other components
export type { WidgetConfig, WidgetButtonConfig, WidgetWindowConfig, WidgetPrechatConfig };
